import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { useLocation } from "react-router-dom";
import { Phone, Menu, X } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Helmet } from "react-helmet-async";
import { SUPPORTED } from "../i18n";
import { LOGO_URL } from "./api";
import { SearchDock } from "./SearchDock";
import { Brand } from "./Brand";
import { useAuth } from "./authApi";
import { FavoriteAccountPrompt } from "./FavoriteAccountPrompt";
import LanguageSelector from "./LanguageSelector";
import { PreFooterCTA } from "./PreFooterCTA";

const NAV = [
  { key: "offers", to: "/biens-off-market" },
  { key: "network", to: "/nos-conseillers-et-agents-immobilier" },
  { key: "philosophy", to: "/philosophie" },
  { key: "expertise", to: "/expertises" },
  { key: "propose", to: "/proposer" },
  { key: "acquereurs", to: "/espace-acquereurs", accent: true },
];

export const TEL = "tel:+33688675357";
export const WHATSAPP_LINK = "https://wa.me/33688675357";

export const WhatsAppIcon = ({ className = "w-4 h-4" }) => (
  <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden="true">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.372-.025-.521-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51l-.57-.01c-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.71.306 1.263.489 1.694.626.712.226 1.36.194 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
  </svg>
);

export const Logo = ({ className = "w-[162px] sm:w-[197px] lg:w-[180px] xl:w-[200px]", viewTrigger = false, compact = false }) => {
  const trigger = viewTrigger
    ? { whileInView: { opacity: 1, y: 0 }, viewport: { once: true, margin: "-40px" } }
    : { animate: { opacity: 1, y: 0 } };
  const base = viewTrigger ? 0 : 0.4;
  const widthCls = compact ? "w-[103px] sm:w-[120px]" : className;
  return (
    <div className={`${widthCls} flex flex-col items-center transition-all duration-500`}>
      <div className="w-full overflow-hidden aspect-[1007/230]">
        <img src={LOGO_URL} alt="OFF MARKETTE" className="w-full h-full object-cover object-top" />
      </div>
      {!compact && (
        <p data-testid="logo-baseline" className="whitespace-nowrap text-center text-[7px] sm:text-[8px] font-bold uppercase tracking-[0.14em] text-white mt-1">
          {"L'immobilier off market par Vincent Marette".split(" ").map((w, i) => (
            <motion.span
              key={i}
              initial={{ opacity: 0, y: 5 }}
              {...trigger}
              transition={{ delay: base + i * 0.08 + (i >= 3 ? (i - 2) * 0.22 : 0), duration: i >= 3 ? 0.75 : 0.4, ease: "easeOut" }}
              className={/^(off|market)$/i.test(w) ? "text-[#C19A6B] mr-1" : "mr-1"}
            >
              {w}
            </motion.span>
          ))}
        </p>
      )}
    </div>
  );
};

export const PlatformLayout = ({ children, searchDock = true, solidHeader = false, glassHeader = false, preFooterCTA = true }) => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { t, i18n } = useTranslation();
  const { pathname } = useLocation();
  const auth = useAuth();
  const user = auth?.user;
  const isMember = user && user.role === "member";
  const isBuyer = user && user.role === "user";
  const isAdmin = user && (user.role === "admin" || user.role === "super_admin");
  const spaceLink = isAdmin ? "/admin" : (isBuyer ? "/espace-acquereurs" : "/mon-compte");
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  const solid = solidHeader || scrolled;

  return (
    <div className="platform-scope bg-[#050505] text-white min-h-screen" style={{ fontFamily: "'Outfit', sans-serif" }}>
      <Helmet htmlAttributes={{ lang: i18n.language || "fr" }}>
        {(() => {
          const origin = (typeof window !== "undefined" && window.location.origin) || "";
          const clean = pathname === "/" ? "" : pathname;
          const urlFor = (l) => l === "fr" ? `${origin}${clean || "/"}` : `${origin}/${l.toLowerCase()}${clean}`;
          return [
            <link key="canonical" rel="canonical" href={urlFor(i18n.language || "fr")} />,
            ...SUPPORTED.map((l) => <link key={`alt-${l}`} rel="alternate" hrefLang={l} href={urlFor(l)} />),
            <link key="alt-x" rel="alternate" hrefLang="x-default" href={urlFor("fr")} />,
          ];
        })()}
      </Helmet>
      <header
        data-testid="platform-header"
        className={`fixed top-0 left-0 w-full z-50 transition-all duration-500 ${glassHeader ? "bg-[#050505]/40 py-4" : solid ? "bg-[#050505] py-2 border-b border-white/5" : "bg-gradient-to-b from-[#050505]/80 to-transparent py-5"}`}
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-12 flex items-center justify-between gap-4 relative">
          <Link to="/" data-testid="platform-logo" className="shrink-0 lg:-ml-[1cm] max-lg:absolute max-lg:left-1/2 max-lg:-translate-x-1/2" onClick={(e) => { if (window.location.pathname === "/") { e.preventDefault(); window.location.reload(); } }}>
            <Logo compact={solid && !glassHeader} />
          </Link>
          <nav className="hidden lg:flex items-center gap-5">
            {NAV.map((n) => (
              <Link key={n.to} to={n.to} data-testid={`nav-${n.to}`} className={`${solid ? "text-[11px]" : "text-[10px]"} uppercase tracking-[0.12em] whitespace-nowrap transition-colors ${n.accent ? "text-[#C19A6B] hover:text-[#d1af84] font-medium" : "text-white hover:text-[#C19A6B]"}`}>
                {t(`nav.${n.key}`)}
              </Link>
            ))}
            {!(isMember || isAdmin || isBuyer) && (
              <Link to="/connexion" data-testid="nav-connexion" className={`${solid ? "text-[11px]" : "text-[10px]"} uppercase tracking-[0.12em] whitespace-nowrap text-white hover:text-[#C19A6B] transition-colors`}>{t("header.login")}</Link>
            )}
            <div className="relative flex items-center gap-3 ml-2">
              <div className="absolute -top-7 right-0" data-testid="header-lang-top"><LanguageSelector /></div>
              {(isMember || isAdmin || isBuyer) ? (
                <Link to={spaceLink} data-testid="nav-mon-espace" className="text-[11px] uppercase tracking-[0.12em] whitespace-nowrap bg-white text-[#050505] border border-white px-3 py-2 hover:bg-transparent hover:text-white transition-colors">{t("header.myspace")}</Link>
              ) : (
                <Link to="/rejoindre" data-testid="nav-inscription" className="text-[10px] uppercase tracking-[0.08em] whitespace-nowrap bg-white text-[#050505] border border-white px-2.5 py-2 animate-pulse hover:animate-none hover:bg-black hover:text-[#C19A6B] hover:border-[#C19A6B] transition-colors">{t("header.join")}</Link>
              )}
              <a href={TEL} data-testid="nav-appeler" className="flex items-center gap-1.5 bg-[#C19A6B] hover:bg-[#d1af84] text-white text-[11px] font-medium uppercase tracking-wider whitespace-nowrap px-4 py-2 transition-colors">
                <Phone className="w-3.5 h-3.5" /> {t("header.call")}
              </a>
            </div>
          </nav>
          <button className="lg:hidden text-white ml-auto" onClick={() => setOpen(!open)} data-testid="nav-toggle">
            {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
        {open && (
          <div className="lg:hidden bg-[#050505] border-t border-white/10 px-6 py-4 space-y-3">
            {NAV.map((n) => (
              <Link key={n.to} to={n.to} onClick={() => setOpen(false)} className={`block text-sm uppercase tracking-wider ${n.accent ? "text-[#C19A6B] font-medium" : "text-white"}`}>{t(`nav.${n.key}`)}</Link>
            ))}
            <a href={TEL} data-testid="nav-appeler-mobile" className="flex items-center gap-2 text-sm uppercase tracking-wider text-[#C19A6B]"><Phone className="w-4 h-4" /> {t("header.call")}</a>
            {(isMember || isAdmin || isBuyer) ? (
              <Link to={spaceLink} onClick={() => setOpen(false)} className="block text-sm uppercase tracking-wider text-white">{t("header.myspace")}</Link>
            ) : (
              <>
                <Link to="/connexion" onClick={() => setOpen(false)} className="block text-sm uppercase tracking-wider text-white">{t("header.login")}</Link>
                <Link to="/rejoindre" onClick={() => setOpen(false)} className="block text-sm uppercase tracking-wider text-[#C19A6B]">{t("header.join")}</Link>
              </>
            )}
            <div className="pt-2 border-t border-white/10 mt-2">
              <p className="text-[11px] uppercase tracking-wider text-zinc-500 mb-2">{t("header.language")}</p>
              <LanguageSelector variant="mobile" />
            </div>
          </div>
        )}
      </header>

      <main>{children}</main>

      <FavoriteAccountPrompt />

      {searchDock && <SearchDock />}

      {preFooterCTA && <PreFooterCTA />}

      <footer className={`border-t border-white/10 bg-[#0A0A0C] pt-16 ${searchDock ? "pb-28 sm:pb-32" : "pb-16"}`}>
        <div className="max-w-7xl mx-auto px-6 lg:px-12 grid grid-cols-1 md:grid-cols-3 gap-10">
          <div>
            <Link to="/" data-testid="footer-logo" className="hidden sm:inline-block mb-4"><Logo viewTrigger /></Link>
            <p className="text-sm font-light text-zinc-500 max-w-xs">Les plus belles opportunités immobilières, souvent avant tout le monde.</p>
          </div>
          <div className="space-y-2">
            <h4 className="text-xs uppercase tracking-[0.2em] text-[#C19A6B] mb-3">Navigation</h4>
            {NAV.map((n) => (<Link key={n.to} to={n.to} className="block text-sm text-zinc-400 hover:text-white transition-colors">{n.to === "/philosophie" ? <Brand title /> : t(`nav.${n.key}`)}</Link>))}
            <Link to="/immobilier-off-market" data-testid="footer-offmarket" className="block text-sm text-zinc-400 hover:text-white transition-colors">L'immobilier off-market</Link>
          </div>
          <div className="space-y-2">
            <h4 className="text-xs uppercase tracking-[0.2em] text-[#C19A6B] mb-3">Contact</h4>
            <p className="text-sm text-zinc-400">Vincent Marette · Conseiller Immobilier</p>
            <a href="tel:+33688675357" className="block text-sm text-white hover:text-[#C19A6B]">+33 6 88 67 53 57</a>
            <a href="mailto:vincent.marette@safti.fr" className="block text-sm text-zinc-400 hover:text-white">vincent.marette@safti.fr</a>
          </div>
        </div>
        <p className="text-center text-xs text-zinc-600 mt-12">© {new Date().getFullYear()} Off Mar<span className="text-white">k</span>ette — Tous droits réservés.</p>
      </footer>
    </div>
  );
};
